import type {
  Task,
  TaskCreate,
  TaskCreateDto,
  TaskDto,
  TaskReference,
  TaskReferenceDto,
  TaskTypeSummary,
  TaskTypeSummaryDto,
  TaskTypeChoice,
  TaskTypeChoiceDto,
  TaskUpdate,
  TaskUpdateDto,
} from '@src/features/tasks/types';
import { shortUserFromDto } from '@src/features/users/lib/user';
import { formatIsoDate } from '@src/shared/lib';

function taskTypeFromDto(taskType: TaskTypeSummaryDto): TaskTypeSummary {
  return {
    id: taskType.id,
    name: taskType.name,
    icon: taskType.icon,
    autoCompletable: taskType.auto_completable,
    assignable: taskType.assignable,
    copyable: taskType.copyable,
  };
}

function referenceFromDto(reference: TaskReferenceDto): TaskReference {
  return {
    contentType: reference.content_type,
    objectId: reference.object_id,
    label: reference.label,
    type: reference.type,
    urlParams: reference.url_params,
    extraDetails: reference.extra_details,
  };
}

export function taskFromDto(task: TaskDto): Task {
  const taskType = taskTypeFromDto(task.task_taty);
  const reference = referenceFromDto(task.reference);

  return {
    id: task.id,
    taskType,
    prioritized: task.task_prioritized,
    dueDate: task.task_due_date,
    dueDateEstimated: task.task_due_date_estimated,
    completionDate: task.task_completion_date,
    status: task.task_status,
    priorities: task.priorities,
    taskUser: shortUserFromDto(task.task_user),
    author: task.task_author ? shortUserFromDto(task.task_author) : null,
    reference,
    canEdit: task.can_edit,
    canReassign: task.can_reassign,
    canCopy: task.can_copy,
    canComplete: task.can_complete,
    editableFields: task.editable_fields,
    description: task.task_description,
    notes: task.task_notes,
    private: task.task_private,
  };
}

export function taskTypeChoiceFromDto(
  taskType: TaskTypeChoiceDto,
): TaskTypeChoice {
  return {
    id: taskType.id,
    name: taskType.name,
    autoGenerated: taskType.taty_auto_generated,
  };
}

function hasOwn<T extends object, K extends PropertyKey>(
  value: T,
  key: K,
): value is T & Record<K, unknown> {
  return Object.hasOwn(value, key);
}

export function taskUpdateToDto(task: Partial<TaskUpdate>): Partial<TaskUpdateDto> {
  const payload: Partial<TaskUpdateDto> = {};

  if (hasOwn(task, 'description')) {
    payload.task_description = task.description as string;
  }
  if (hasOwn(task, 'dueDate')) {
    payload.task_due_date = formatIsoDate(task.dueDate as Date | string);
  }
  if (hasOwn(task, 'notes')) {
    payload.task_notes = task.notes as string;
  }

  return payload;
}

export function taskCreateToDto(task: TaskCreate): TaskCreateDto {
  const payload: TaskCreateDto = {
    task_taty: task.taskTypeId,
    task_due_date: formatIsoDate(task.dueDate),
    task_due_date_estimated: task.dueDateEstimated,
    task_user: task.taskUserId,
    task_description: task.description,
    task_notes: task.notes,
    task_private: task.private,
  };

  if (
    task.reference
    && task.reference.contentTypeId !== null
    && task.reference.objectId !== null
  ) {
    payload.content_type_id = task.reference.contentTypeId;
    payload.object_id = task.reference.objectId;
  }

  return payload;
}
